import {
  assert,
  BaseNumber,
  bigNumberify,
  CheckedAddress,
  NormalizedNumber,
  Percentage,
  raise,
} from '@sparkdotfi/common-universal'
import BigNumber from 'bignumber.js'

import { AaveDataLayerQueryReturnType, aaveDataLayerSelectFn } from './aave-data-layer/query'
import { determineEModeState, extractEmodeInfoFromReserves } from './emode'
import { getIncentivesData, IncentivesData } from './incentives'
import { parseRawPercentage } from './math'
import {
  BorrowEligibilityStatus,
  CollateralEligibilityStatus,
  getBorrowEligibilityStatus,
  getCollateralEligibilityStatus,
  getReserveStatus,
  getSupplyAvailabilityStatus,
  ReserveStatus,
  SupplyAvailabilityStatus,
} from './reserve-status'
import {
  determineIsolationModeState,
  determineSiloBorrowingState,
  normalizeUserSummary as normalizeUserPositionSummary,
} from './utils'
import { getChainConfigEntry } from '@/config/chain'
import { NativeAssetInfo } from '@/config/chain/types'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { SparkLendToken } from '@/features/sparklend/tokens/SparkLendToken'
import { fromRay } from '@/utils/math'

export interface Reserve {
  token: SparkLendToken

  aToken: SparkLendToken
  variableDebtTokenAddress: CheckedAddress

  status: ReserveStatus

  supplyAvailabilityStatus: SupplyAvailabilityStatus
  collateralEligibilityStatus: CollateralEligibilityStatus
  borrowEligibilityStatus: BorrowEligibilityStatus

  isIsolated: boolean
  isBorrowableInIsolation: boolean // in practice this is true only for stablecoins
  isSiloedBorrowing: boolean
  eModeCategory?: EModeCategory

  // @note: available liquidity respects borrow cap, so it can be negative when the cap is reached and breached (interests)
  availableLiquidity: NormalizedNumber
  availableLiquidityUSD: NormalizedNumber
  unborrowedLiquidity: NormalizedNumber
  supplyCap?: NormalizedNumber
  borrowCap?: NormalizedNumber
  totalLiquidity: NormalizedNumber // = supplied
  totalLiquidityUSD: NormalizedNumber
  totalDebt: NormalizedNumber
  totalDebtUSD: NormalizedNumber
  totalVariableDebt: NormalizedNumber
  totalVariableDebtUSD: NormalizedNumber
  isolationModeTotalDebt: NormalizedNumber // @note: this is already divided by debtCeilingDecimals
  debtCeiling: NormalizedNumber // @note: this is already divided by debtCeilingDecimals
  supplyAPY: Percentage | undefined
  maxLtv: Percentage
  liquidationThreshold: Percentage
  liquidationBonus: Percentage
  reserveFactor: Percentage
  aTokenBalance: NormalizedNumber
  unbacked: NormalizedNumber

  lastUpdateTimestamp: number

  variableBorrowIndex: BigNumber
  variableBorrowRate: BigNumber
  liquidityIndex: BigNumber
  liquidityRate: BigNumber
  variableRateSlope1: BigNumber
  variableRateSlope2: BigNumber
  optimalUtilizationRate: Percentage
  utilizationRate: Percentage
  baseVariableBorrowRate: BigNumber

  interestRateStrategyAddress: CheckedAddress

  variableBorrowApy: Percentage | undefined

  priceInUSD: BigNumber
  priceOracle: CheckedAddress
  usageAsCollateralEnabled: boolean
  usageAsCollateralEnabledOnUser: boolean

  incentives: IncentivesData

  sllManaged: boolean
}

export interface UserPosition {
  reserve: Reserve
  scaledVariableDebt: BigNumber
  scaledATokenBalance: BigNumber
  collateralBalance: NormalizedNumber
  borrowBalance: NormalizedNumber
}

export interface UserPositionSummary {
  loanToValue: Percentage
  maxLoanToValue: Percentage
  healthFactor: BigNumber | undefined
  availableBorrowsUSD: NormalizedNumber
  totalBorrowsUSD: NormalizedNumber
  currentLiquidationThreshold: Percentage
  totalCollateralUSD: NormalizedNumber
  totalLiquidityUSD: NormalizedNumber
}

export type EModeState = { enabled: false } | { enabled: true; category: EModeCategory }
export type SiloBorrowingState = { enabled: false } | { enabled: true; siloedBorrowingReserve: Reserve }
export type IsolatedBorrowingState = { enabled: false } | { enabled: true; isolatedBorrowingReserve: Reserve }

export interface UserConfiguration {
  eModeState: EModeState
  isolationModeState: IsolatedBorrowingState
  siloBorrowingState: SiloBorrowingState
}

export interface EModeCategory {
  id: number
  name: string
  ltv: Percentage
  liquidationThreshold: Percentage
  liquidationBonus: Percentage
}
export type EModeCategories = Record<number, EModeCategory>

export interface UserReward {
  assets: CheckedAddress[]
  value: NormalizedNumber
  token: SparkLendToken
  incentiveControllerAddress: CheckedAddress
}

export interface ReserveWithValue {
  reserve: Reserve
  value: NormalizedNumber
}

export class MarketInfo {
  private readonly nativePosition: UserPosition

  constructor(
    public readonly reserves: Reserve[],
    public readonly userPositions: UserPosition[], // exists for every reserve, even if empty
    public readonly userPositionSummary: UserPositionSummary,
    public readonly userConfiguration: UserConfiguration,
    public readonly emodeCategories: EModeCategories,
    public readonly timestamp: number,
    public readonly chainId: number,
    public readonly userRewards: UserReward[],
    public readonly nativeAssetInfo: NativeAssetInfo,
  ) {
    const wrappedNativeAssetPosition =
      userPositions.find((p) => p.reserve.token.symbol === nativeAssetInfo.wrappedNativeAssetSymbol) ??
      raise('Wrapped native reserve not found.')
    const wrappedNativeToken = wrappedNativeAssetPosition.reserve.token
    const nativeToken = wrappedNativeToken.clone({
      symbol: nativeAssetInfo.nativeAssetSymbol,
      name: nativeAssetInfo.nativeAssetName,
      logo: nativeAssetInfo.nativeAssetLogo,
      address: CheckedAddress.EEEE(),
    })
    const nativeReserve = {
      ...wrappedNativeAssetPosition.reserve,
      token: nativeToken,
    }
    this.nativePosition = {
      ...wrappedNativeAssetPosition,
      reserve: nativeReserve,
    }
  }

  findBySymbol(symbol: TokenSymbol): SparkLendToken | undefined {
    return this.findReserveByATokenSymbol(symbol)?.aToken ?? this.findReserveBySymbol(symbol)?.token
  }
  findOneBySymbol(symbol: TokenSymbol): SparkLendToken {
    return this.findBySymbol(symbol) ?? raise(`Token ${symbol} not found`)
  }

  findReserveBySymbol(symbol: TokenSymbol): Reserve | undefined {
    if (symbol === this.nativeAssetInfo.nativeAssetSymbol) {
      return this.nativePosition.reserve
    }

    return this.findReserveByATokenSymbol(symbol) ?? this.reserves.find((r) => r.token.symbol === symbol)
  }
  findReserveByToken(token: SparkLendToken): Reserve | undefined {
    return this.findReserveBySymbol(token.symbol)
  }
  findReserveByUnderlyingAsset(underlyingAsset: CheckedAddress): Reserve | undefined {
    if (underlyingAsset === CheckedAddress.EEEE()) {
      return this.nativePosition.reserve
    }

    return this.reserves.find((r) => r.token.address === underlyingAsset)
  }
  findReserveByATokenSymbol(symbol: TokenSymbol): Reserve | undefined {
    return this.reserves.find((r) => r.aToken.symbol === symbol)
  }
  findOneReserveBySymbol(symbol: TokenSymbol): Reserve {
    return this.findReserveBySymbol(symbol) ?? raise(`Reserve ${symbol} not found`)
  }
  findOneReserveByToken(token: SparkLendToken): Reserve {
    return this.findOneReserveBySymbol(token.symbol)
  }
  findOneReserveByUnderlyingAsset(underlyingAsset: CheckedAddress): Reserve {
    return this.findReserveByUnderlyingAsset(underlyingAsset) ?? raise(`Reserve ${underlyingAsset} not found`)
  }

  findPositionBySymbol(symbol: TokenSymbol): UserPosition | undefined {
    if (symbol === this.nativeAssetInfo.nativeAssetSymbol) {
      return this.nativePosition
    }

    return this.findPositionByATokenSymbol(symbol) ?? this.userPositions.find((p) => p.reserve.token.symbol === symbol)
  }
  findPositionByToken(token: SparkLendToken): UserPosition | undefined {
    return this.findPositionBySymbol(token.symbol)
  }
  findPositionByATokenSymbol(symbol: TokenSymbol): UserPosition | undefined {
    return this.userPositions.find((p) => p.reserve.aToken.symbol === symbol)
  }
  findOnePositionBySymbol(symbol: TokenSymbol): UserPosition {
    return this.findPositionBySymbol(symbol) ?? raise(`Position ${symbol} not found`)
  }
  findOnePositionByToken(token: SparkLendToken): UserPosition {
    return this.findOnePositionBySymbol(token.symbol)
  }
}

export interface MarketInfoSelectFnParams {
  timeAdvance?: number // time advance in seconds
}

export function marketInfoSelectFn({ timeAdvance }: MarketInfoSelectFnParams = {}) {
  return (data: AaveDataLayerQueryReturnType) => {
    const rawAaveData = aaveDataLayerSelectFn({ timeAdvance })(data)
    const chainId = data.chainId
    const { sparkLend, nativeAssetInfo, tokenDefinitionRepository } = getChainConfigEntry(chainId)
    assert(sparkLend, 'SparkLend config not found')

    const tokens = rawAaveData.userSummary.userReservesData.map((r): SparkLendToken => {
      const symbol = TokenSymbol(r.reserve.symbol)
      const tokenDefinition = tokenDefinitionRepository.findOneBySymbol(symbol)

      assert(
        tokenDefinition.address === CheckedAddress(r.reserve.underlyingAsset) &&
          tokenDefinition.decimals === r.reserve.decimals,
        `Token definition mismatch for ${symbol}`,
      )

      return new SparkLendToken({
        address: tokenDefinition.address,
        symbol: tokenDefinition.symbol,
        name: tokenDefinition.name,
        decimals: tokenDefinition.decimals,
        logo: tokenDefinition.logo,
        unitPriceUsd: NormalizedNumber(r.reserve.priceInUSD),
      })
    })

    /* eslint-disable func-style */
    const findOneBySymbol = (symbol: TokenSymbol): SparkLendToken => {
      return tokens.find((t) => t.symbol === symbol) ?? raise(`Token ${symbol} not found`)
    }
    /* eslint-enable func-style */

    const eModeCategories = extractEmodeInfoFromReserves(rawAaveData.formattedReserves)

    const reserves = rawAaveData.userSummary.userReservesData.map((r): Reserve => {
      const token = findOneBySymbol(TokenSymbol(r.reserve.symbol))
      const supplyAvailabilityStatus = getSupplyAvailabilityStatus(r.reserve)
      const collateralEligibilityStatus = getCollateralEligibilityStatus(r.reserve)
      const borrowEligibilityStatus = getBorrowEligibilityStatus(r.reserve)
      const sllManaged = sparkLend.sllManagedMarkets.includes(TokenSymbol(r.reserve.symbol))

      return {
        token,

        aToken: token.createAToken(CheckedAddress(r.reserve.aTokenAddress)),
        variableDebtTokenAddress: CheckedAddress(r.reserve.variableDebtTokenAddress),

        status: getReserveStatus(r.reserve),

        supplyAvailabilityStatus,
        collateralEligibilityStatus,
        borrowEligibilityStatus,

        isIsolated: r.reserve.isIsolated,
        eModeCategory:
          r.reserve.eModeCategoryId !== 0
            ? (eModeCategories[r.reserve.eModeCategoryId] ??
              raise(`EMode category ${r.reserve.eModeCategoryId} not found`))
            : undefined,
        isSiloedBorrowing: r.reserve.isSiloedBorrowing,
        isBorrowableInIsolation: r.reserve.borrowableInIsolation,

        availableLiquidity: NormalizedNumber(r.reserve.formattedAvailableLiquidity), // @note: r.reserve.availableLiquidity doesn't respect borrow caps so we use formattedAvailableLiquidity which does
        availableLiquidityUSD: NormalizedNumber(r.reserve.availableLiquidityUSD),
        unborrowedLiquidity: NormalizedNumber(r.reserve.unborrowedLiquidity),
        supplyCap: r.reserve.supplyCap !== '0' ? NormalizedNumber(r.reserve.supplyCap) : undefined,
        borrowCap: r.reserve.borrowCap !== '0' ? NormalizedNumber(r.reserve.borrowCap) : undefined,
        totalLiquidity: NormalizedNumber(r.reserve.totalLiquidity),
        totalLiquidityUSD: NormalizedNumber(r.reserve.totalLiquidityUSD),
        totalDebt: NormalizedNumber(r.reserve.totalDebt),
        totalDebtUSD: NormalizedNumber(r.reserve.totalDebtUSD),
        totalVariableDebt: NormalizedNumber(r.reserve.totalVariableDebt),
        totalVariableDebtUSD: NormalizedNumber(r.reserve.totalVariableDebtUSD),
        isolationModeTotalDebt: NormalizedNumber(r.reserve.isolationModeTotalDebtUSD),
        debtCeiling: NormalizedNumber(r.reserve.debtCeilingUSD),
        supplyAPY: supplyAvailabilityStatus === 'no' ? undefined : Percentage(r.reserve.supplyAPY), // when supplying is disabled, APY is not available
        maxLtv: parseRawPercentage(r.reserve.baseLTVasCollateral),
        liquidationThreshold: parseRawPercentage(r.reserve.reserveLiquidationThreshold),
        liquidationBonus: bigNumberify(r.reserve.formattedReserveLiquidationBonus).gt(0)
          ? Percentage(r.reserve.formattedReserveLiquidationBonus)
          : Percentage(0),
        variableBorrowApy: borrowEligibilityStatus === 'no' ? undefined : Percentage(r.reserve.variableBorrowAPY), // when borrowing is disabled, APY is not available
        reserveFactor: Percentage(r.reserve.reserveFactor),
        aTokenBalance: NormalizedNumber(r.underlyingBalance),
        unbacked: NormalizedNumber(r.reserve.unbacked),

        lastUpdateTimestamp: r.reserve.lastUpdateTimestamp,

        variableBorrowIndex: bigNumberify(r.reserve.variableBorrowIndex),
        variableBorrowRate: bigNumberify(r.reserve.variableBorrowRate),
        liquidityIndex: bigNumberify(r.reserve.liquidityIndex),
        liquidityRate: bigNumberify(r.reserve.liquidityRate),
        variableRateSlope1: bigNumberify(r.reserve.variableRateSlope1),
        variableRateSlope2: bigNumberify(r.reserve.variableRateSlope2),
        optimalUtilizationRate: Percentage(fromRay(r.reserve.optimalUsageRatio)),
        utilizationRate: Percentage(r.reserve.borrowUsageRatio),
        baseVariableBorrowRate: bigNumberify(r.reserve.baseVariableBorrowRate),

        interestRateStrategyAddress: CheckedAddress(r.reserve.interestRateStrategyAddress),

        priceInUSD: bigNumberify(r.reserve.priceInUSD),
        priceOracle: CheckedAddress(r.reserve.priceOracle),

        usageAsCollateralEnabled: r.reserve.usageAsCollateralEnabled,
        usageAsCollateralEnabledOnUser: r.usageAsCollateralEnabledOnUser,

        // incentives are fetched from the blockchain
        incentives: getIncentivesData(r.reserve, findOneBySymbol),

        sllManaged,
      }
    })

    const userPositions = rawAaveData.rawUserReserves.map((r): UserPosition => {
      const reserve = reserves.find((res) => res.token.address === r.underlyingAsset)!
      const formattedReserve = rawAaveData.userSummary.userReservesData.find(
        (res) => res.underlyingAsset === r.underlyingAsset,
      )!

      return {
        reserve,
        scaledATokenBalance: bigNumberify(r.scaledATokenBalance),
        scaledVariableDebt: bigNumberify(r.scaledVariableDebt),
        collateralBalance: NormalizedNumber(formattedReserve.underlyingBalance),
        borrowBalance: NormalizedNumber(formattedReserve.variableBorrows),
      }
    })

    const userPositionSummary = normalizeUserPositionSummary(rawAaveData.userSummary)

    const userConfiguration: UserConfiguration = {
      eModeState: determineEModeState(rawAaveData.userEmodeCategoryId, eModeCategories),
      isolationModeState: determineIsolationModeState(rawAaveData.userSummary, reserves),
      siloBorrowingState: determineSiloBorrowingState(userPositions),
    }

    const allIncentiveEligibleAddresses: CheckedAddress[] = rawAaveData.userSummary.userReservesData.flatMap(
      ({ reserve }) =>
        [
          { data: reserve.aIncentivesData, address: reserve.aTokenAddress },
          { data: reserve.vIncentivesData, address: reserve.variableDebtTokenAddress },
          { data: reserve.sIncentivesData, address: reserve.stableDebtTokenAddress },
        ]
          .filter(({ data }) => data && data.length > 0)
          .map(({ address }) => CheckedAddress(address)),
    )

    const userRewards: UserReward[] = Object.values(rawAaveData.userRewards)
      .map((value) => {
        const token = findOneBySymbol(TokenSymbol(value.rewardTokenSymbol))
        return {
          value: token.fromBaseUnit(BaseNumber(value.claimableRewards.dp(0))),
          token,
          incentiveControllerAddress: CheckedAddress(value.incentiveControllerAddress),
          assets: allIncentiveEligibleAddresses, // overriding rewards assets to ensure that all possible rewards are claimed
        }
      })
      .filter((r) => r.value.gt(0))

    return new MarketInfo(
      reserves,
      userPositions,
      userPositionSummary,
      userConfiguration,
      eModeCategories,
      rawAaveData.timestamp,
      chainId,
      userRewards,
      nativeAssetInfo,
    )
  }
}
